<template>
  <div class="chat-input-area" v-show="!aiResultMode && !thinkingMode">
    <template v-if="isLoadingCompanyInfo">
      <div class="loading-container">
        <i class="el-icon-loading"></i>
        <span>企業情報を取得中...</span>
      </div>
    </template>

    <template
      v-else-if="
        currentQuestionIndex < questionsLength && !allQuestionsCompleted
      "
    >
      <template v-if="currentQuestion && currentQuestion.type === 'url'">
        <div class="quick-reply-container">
          <button class="quick-reply-button" @click="$emit('skip-url')">
            スキップする
          </button>
        </div>
        <div class="input-container">
          <input
            type="url"
            :placeholder="
              currentQuestion.placeholder || 'URLを入力してください'
            "
            :value="currentAnswer"
            @input="onAnswerInput"
            @keydown.enter="$emit('enter', $event)"
            @compositionstart="$emit('composition', true)"
            @compositionend="$emit('composition', false)"
            class="chat-input-field"
          />
          <button class="send-button" @click="$emit('submit-url')">
            <img src="/static/img/yagichat/send-icon.svg" alt="" />
          </button>
        </div>
        <div class="progress-info">
          <span class="progress-text"
            >質問 {{ currentQuestionIndex + 1 }} / {{ questionsLength }}</span
          >
        </div>
      </template>

      <template
        v-else-if="
          currentQuestion &&
          currentQuestion.quickReplies &&
          currentQuestion.quickReplies.length > 0
        "
      >
        <div class="quick-reply-container quick-reply-choices">
          <button
            v-for="(reply, idx) in currentQuestion.quickReplies"
            :key="idx"
            class="quick-reply-button"
            @click="$emit('quick-reply', reply)"
          >
            {{ reply.label }}
          </button>
        </div>
        <div class="progress-info">
          <span class="progress-text"
            >質問 {{ currentQuestionIndex + 1 }} / {{ questionsLength }}</span
          >
        </div>
      </template>

      <template v-else>
        <div
          v-if="currentQuestion && currentQuestion.allowNone"
          class="quick-reply-container"
        >
          <button class="quick-reply-button" @click="$emit('submit-none')">
            なし
          </button>
        </div>
        <div class="input-container">
          <input
            type="text"
            :placeholder="
              (currentQuestion && currentQuestion.placeholder) ||
              '回答を入力してください'
            "
            :value="currentAnswer"
            @input="onAnswerInput"
            @keydown.enter="$emit('enter', $event)"
            @compositionstart="$emit('composition', true)"
            @compositionend="$emit('composition', false)"
            class="chat-input-field"
          />
          <button class="send-button" @click="$emit('submit')">
            <img src="/static/img/yagichat/send-icon.svg" alt="" />
          </button>
        </div>
        <div class="progress-info">
          <span class="progress-text"
            >質問 {{ currentQuestionIndex + 1 }} / {{ questionsLength }}</span
          >
          <span v-if="showUsageCount && usageCount !== null" class="usage-text">
            {{ usageCountLabel }}: {{ usageCount }} / {{ maxUsageCount }}回
          </span>
        </div>
      </template>
    </template>

    <template v-else>
      <div class="all-filled-container">
        <div class="all-filled-message">
          <i class="el-icon-success"></i>
          <span>全ての情報が入力されています</span>
        </div>
        <el-button
          type="success"
          class="generate-button"
          @click="$emit('complete')"
        >
          {{ completeButtonText }}
        </el-button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  YagiChatQuestion,
  YagiChatQuickReply
} from '@/utils/format-edit/yagiChatBot'

defineProps<{
  aiResultMode: boolean
  thinkingMode: boolean
  isLoadingCompanyInfo: boolean
  currentQuestionIndex: number
  questionsLength: number
  allQuestionsCompleted: boolean
  currentQuestion: YagiChatQuestion | null
  currentAnswer: string
  showUsageCount: boolean
  usageCount: number | null
  maxUsageCount: number
  usageCountLabel: string
  completeButtonText: string
}>()

const emit = defineEmits<{
  (e: 'update:currentAnswer', value: string): void
  (e: 'skip-url'): void
  (e: 'submit-url'): void
  (e: 'submit'): void
  (e: 'submit-none'): void
  (e: 'quick-reply', reply: YagiChatQuickReply): void
  (e: 'enter', event: KeyboardEvent): void
  (e: 'composition', value: boolean): void
  (e: 'complete'): void
}>()

function onAnswerInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:currentAnswer', target.value)
}
</script>

<style lang="scss" scoped>
.chat-input-area {
  background: white;
  padding: 15px 20px;

  .input-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .chat-input-field {
    flex: 1;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 10px;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #2d8b6f;
    }
  }

  .send-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      transform: scale(1.1);
      opacity: 1;
    }
  }

  .quick-reply-container {
    display: flex;
    justify-content: center;
    padding: 8px 0;
    gap: 8px;

    &.quick-reply-choices {
      flex-wrap: wrap;
      padding: 12px;
    }

    .quick-reply-button {
      background-color: #fff;
      border: 1px solid #0f9934;
      border-radius: 18px;
      padding: 8px 16px;
      font-size: 0.85rem;
      color: #0f9934;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background-color: #0f9934;
        color: #fff;
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .progress-info {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 0.75rem;
    color: #999;

    .progress-text {
      font-weight: 500;
    }

    .usage-text {
      color: #666;
    }
  }

  .all-filled-container {
    text-align: center;
    padding: 10px 0;

    .all-filled-message {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 15px;
      color: #27ae60;
      font-weight: 500;
      font-size: 0.9rem;

      i {
        font-size: 1.2rem;
      }
    }

    .generate-button {
      width: 100%;
      font-size: 1rem;
      padding: 12px 0;
      font-weight: bold;
    }
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 20px 0;
    color: #27ae60;
    font-size: 0.9rem;

    i {
      font-size: 1.5rem;
    }
  }
}
</style>
